import { Inject, Injectable, signal } from '@angular/core'
import maplibregl, { GeoJSONSource, LngLat, Map, MapGeoJSONFeature } from 'maplibre-gl'
import { XyzTileModel } from '../../shared/models/xyz-tile.model'
import { Subject } from 'rxjs'
import { BBox, FeatureCollection } from 'geojson'
import { GeocodingControl } from '@maptiler/geocoding-control/maplibregl'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from '../models'
import { Layers } from 'src/app/shared/layers-config/layers'
import { SpriteService } from './sprite-service' // Add this import
import { SphericalMercator } from '@mapbox/sphericalmercator'
import { Theme, ThemeMode } from '../../shared/services/theme.service'

@Injectable({
  providedIn: 'root',
})
export class MapService {
  private readonly themeModeStorageKey = 'app-theme-mode'
  private sphericalMercator: SphericalMercator
  private currentXyzTiles: XyzTileModel[] = []
  private currentCenter: LngLat = new LngLat(0, 0)
  private currentZoom: number | undefined
  private readonly systemThemeQuery = window.matchMedia('(prefers-color-scheme: dark)')
  public mapViewChanged = new Subject<XyzTileModel[]>()
  public mapReadyChanged = new Subject<void>()
  public isMapReady = false
  public readonly isOsmEnabled = signal(false)
  public selectedTheme = (window.localStorage.getItem('theme') as Theme) ?? 'dark'

  public gc = new GeocodingControl({
    apiKey: this.config.maptiler_key,
    marker: true,
    limit: 4,
    minLength: 2,
    fuzzyMatch: false,
    debounceSearch: 300,
    language: ['de', 'en'],
    collapsed: false,
    noResultsMessage: 'No matching location found. Format: lat/lon always 3 decimals',
    errorMessage: 'No matching location found. Format: lat/lon always 3 decimals',
    types: ['neighbourhood', 'place', 'postal_code', 'poi'],
    excludeTypes: true,
  })
  public markerClassName = ''
  public el = document.createElement('div')
  public marker = new maplibregl.Marker({
    anchor: 'bottom',
    element: this.el,
    draggable: false,
  })

  public map!: Map
  private useSprites = true // Enable sprite usage
  private spritesLoaded = false

  constructor(
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
    private spriteService: SpriteService, // Inject sprite service
  ) {
    this.sphericalMercator = new SphericalMercator({ size: 256 })
    this.systemThemeQuery.addEventListener('change', this.onSystemThemeChanged)
  }

  private readonly onSystemThemeChanged = (): void => {
    if (this.getThemeMode() !== 'system' || this.isOsmEnabled()) {
      return
    }

    this.selectedTheme = this.getSystemTheme()
    window.localStorage.setItem('theme', this.selectedTheme)
    this.applyMapThemeFromSelection()
  }

  private calcXyzTiles(minZoom?: number, maxZoom?: number): XyzTileModel[] {
    const xyzTiles: XyzTileModel[] = []

    if (this.map) {
      let zoom = Math.floor(this.map.getZoom())
      // if there is a max zoom provided don't return tiles for larger zoom levels
      zoom = maxZoom !== undefined ? Math.min(zoom, maxZoom) : zoom
      zoom = minZoom !== undefined ? Math.max(zoom, minZoom) : zoom
      const tiles = this.sphericalMercator.xyz(
        [
          this.map.getBounds().getWest(),
          this.map.getBounds().getSouth(),
          this.map.getBounds().getEast(),
          this.map.getBounds().getNorth(),
        ],
        zoom,
      )
      for (let x = tiles.minX; x <= tiles.maxX; x++) {
        for (let y = tiles.minY; y <= tiles.maxY; y++) {
          xyzTiles.push({
            x: x,
            y: y,
            z: zoom,
          })
        }
      }
    }

    return xyzTiles
  }

  public initMap(map: Map) {
    window.map = map
    this.map = map

    // Set map reference in sprite service
    this.spriteService.setMap(map)

    this.map.on('moveend', () => {
      this.updateMapTiles()
    })
    this.map.on('zoomend', () => {
      this.updateMapTiles()
    })
    this.map.on('load', async () => {
      if (this.map.loaded()) {
        await this.loadImages()
        this.isMapReady = true
        this.applyMapThemeFromSelection()
        this.updateMapTiles(false)
        this.mapReadyChanged.next()
      }
    })
  }

  // Updated loadImages method to use sprites
  private async loadImages(): Promise<any> {
    if (this.useSprites) {
      try {
        // Load all sprite types for maximum compatibility
        await this.spriteService.loadAllTypes()

        this.spritesLoaded = true

        // Log sprite statistics for debugging
        console.log('Sprite stats:', this.spriteService.getSpriteStats())
        return
      } catch (error) {
        this.useSprites = false
        this.spritesLoaded = false
      }
    }
  }

  public getXyzTiles(maxZoom?: number, minZoom?: number) {
    if (minZoom && maxZoom) {
      return this.calcXyzTiles(minZoom, maxZoom)
    } else if (maxZoom) {
      return this.calcXyzTiles(maxZoom)
    }
    return this.currentXyzTiles
  }

  public setPosition(lat?: number, lon?: number, zoom?: number) {
    if (lat && lon && this.currentCenter.lat != lat && this.currentCenter.lng != lon) {
      this.currentCenter = new LngLat(lon, lat)
      this.map.setCenter(this.currentCenter)
    }
    if (zoom && this.currentZoom != zoom) {
      this.currentZoom = zoom
      this.map.setZoom(this.currentZoom)
    }
  }

  public setLayerVisibility(layerId: string, isVisible: boolean) {
    this.map.setLayoutProperty(layerId, 'visibility', isVisible ? 'visible' : 'none')
  }

  public getLayerVisibilty(layerId: string) {
    const layer = this.map.getLayer(layerId)
    if (layer && layer.visibility === 'visible') {
      return true
    }
    return false
  }

  public updateMapTiles(emitEvent = true): void {
    this.currentXyzTiles = this.calcXyzTiles()
    this.currentCenter = this.map.getCenter()
    this.currentZoom = this.map.getZoom()
    if (emitEvent) {
      this.mapViewChanged.next(this.currentXyzTiles)
    }

    // if (!isEqual(this.currentXyzTiles, xyzTiles) || forceUpdate) {
    //   this.currentXyzTiles = xyzTiles
    //   this.xyzTileSubject.next(xyzTiles)
    // }
  }

  public setGeoJsonData(sourceId: string, featureCollection: FeatureCollection) {
    const source: GeoJSONSource | undefined = this.map.getSource(sourceId) as GeoJSONSource
    if (source) {
      source.setData(featureCollection)
    }
  }

  public getFeatures(layerId: string, bbox: BBox): MapGeoJSONFeature[] {
    const coordSw = this.map.project([bbox[0], bbox[1]])
    const coordNe = this.map.project([bbox[2], bbox[3]])

    const features = this.map.queryRenderedFeatures([coordSw, coordNe], {
      layers: [layerId],
    })

    if (features && features.length > 0) {
      return features.filter(
        (obj, index) => features.findIndex((item) => item.id === obj.id) === index,
      )
    }
    return []
  }

  public activateGeoLocationSearch() {
    this.map.addControl(this.gc, 'top-left')
    return undefined
  }

  public deactivateGeoLocationSearch() {
    this.map.removeControl(this.gc as any)
    return undefined
  }

  public changeMapStyle(style: string) {
    const layerCfg = {
      transformStyle: (previousStyle: any, nextStyle: any) => {
        var custom_layers = previousStyle
          ? previousStyle.layers.filter((layer: any) => {
              let layers = Object.values(Layers).concat([
                { name: 'internal' },
                { name: 'customer' },
              ])
              return layers.find((entry) => {
                return layer.id.substring(0, entry.name.length).includes(entry.name)
              })
            })
          : []
        var layers = nextStyle.layers.concat(custom_layers)
        var sources = nextStyle.sources
        if (previousStyle) {
          for (const [key, value] of Object.entries(previousStyle.sources)) {
            if (key) {
              sources[key] = value
            }
          }
        }
        return {
          ...nextStyle,
          sources: sources,
          layers: layers,
        }
      },
    }
    this.map.setStyle(style, layerCfg)
  }

  public switchOsmView(): void {
    this.isOsmEnabled.update((enabled) => !enabled)

    if (this.isOsmEnabled()) {
      this.changeMapStyle(`${this.config.map_style_urls.osm}?key=${this.config.maptiler_key}`)
      return
    }

    this.applyMapThemeFromSelection()
  }

  public applyMapThemeFromSelection(): void {
    const themeMode = this.getThemeMode()
    this.selectedTheme = themeMode === 'system' ? this.getSystemTheme() : themeMode

    window.localStorage.setItem('theme', this.selectedTheme)

    if (this.isOsmEnabled() || !this.isMapReady) {
      return
    }

    this.changeMapStyle(
      `${this.config.map_style_urls[this.selectedTheme]}?key=${this.config.maptiler_key}`,
    )
  }

  private getThemeMode(): ThemeMode {
    const savedThemeMode = window.localStorage.getItem(this.themeModeStorageKey)

    return savedThemeMode === 'system' || savedThemeMode === 'light' || savedThemeMode === 'dark'
      ? savedThemeMode
      : 'system'
  }

  private getSystemTheme(): Theme {
    return this.systemThemeQuery.matches ? 'dark' : 'light'
  }

  // Add a method to expand bounds
  public getExpandedBounds(expansionFactor: number = 0.2): maplibregl.LngLatBounds {
    const bounds = this.map.getBounds()
    const sw = bounds.getSouthWest()
    const ne = bounds.getNorthEast()

    const lngDiff = ne.lng - sw.lng
    const latDiff = ne.lat - sw.lat

    const expandedSW = new maplibregl.LngLat(
      sw.lng - lngDiff * expansionFactor,
      sw.lat - latDiff * expansionFactor,
    )

    const expandedNE = new maplibregl.LngLat(
      ne.lng + lngDiff * expansionFactor,
      ne.lat + latDiff * expansionFactor,
    )

    return new maplibregl.LngLatBounds(expandedSW, expandedNE)
  }
}
